
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { NavigationGuardNext, Route } from 'vue-router';
import { Storage } from '@/services/auth/storage';
import { PINROLES } from '@/utils/constants';
import { PERMISSION } from '@/permissions';
import { decodeString } from '@/utils/decoding';
import { toErrorMessage } from '@/utils/errorHandling';
import { buildMemberInfoFromProvider } from '@/utils/membership-helpers';
import Toast from '@/utils/toast';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import OrderCart from '@/components/orders/cart/OrderCart.vue';
import OrderDetailsModal from '@/components/orders/modals/OrderDetailsModal.vue';
import OrderGuestDetails from '@/components/orders/modals/GuestDetailsModal.vue';
import OrderItemViewer from '@/components/orders/item-viewer/OrderItemViewer.vue';
import OrderMenus from '@/components/orders/menu/OrderMenus.vue';
import OrderSubmittedModal from '@/components/orders/modals/OrderSubmittedModal.vue';
import CheckinQuestionModal from '@/components/orders/modals/CheckinQuestionModal.vue';
import PinScreen from '@/components/orders/menu/PinScreen.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import KioskWelcomeModal from '@/components/order-management/KioskWelcomeModal.vue';
import PageLoader from '@/components/shared/PageLoader.vue';

const namespace: string = 'orders';

@Component<Orders>({
	components: {
		FullPageSpinner,
		OrderCart,
		OrderDetailsModal,
		OrderGuestDetails,
		OrderItemViewer,
		OrderMenus,
		OrderSubmittedModal,
		CheckinQuestionModal,
		PinScreen,
		StandardModal,
		KioskWelcomeModal,
		PageLoader
	}
})
export default class Orders extends Vue {
	@Action('fetchAndSetOrderInfo', { namespace }) fetchAndSetOrderInfo!: (orderId: string) => void;
	@Action('setRestaurantCharges', { namespace }) setRestaurantCharges!: (restaurant: Restaurant) => void;
	@Action('setModifyingOrder', { namespace }) setModifyingOrder!: (isModifying: boolean) => void;
	@Action('setIsQuoting', { namespace }) setIsQuoting!: (isQuoting: boolean) => void;
	@Action('setOrderDetails', { namespace }) setOrderDetails!: (orderDetails: OrderDetails) => void;
	@Action('resetOrderState', { namespace }) resetOrderState!: () => void;
	@Action('lookupMembership', { namespace: 'orders' }) private lookupMembership!: ({ identifier } : { identifier: string }) => Promise<MembershipLookupResult | undefined>;
	@Action('applyPromoCode', { namespace: 'orders' }) private applyPromoCode!: ({ memberInfo, menus } : { memberInfo: CheckoutMemberInfo; menus: Menu[] }) => Promise<void>;
	@Action('fetchPublishedMenus', { namespace: 'menus' }) fetchPublishedMenus!: () => void;
	@Action('cleanupOpenItems', { namespace: 'menus' }) private cleanupOpenItems!: () => Promise<void>;
	@Action('fetchOrderModificationRequestById', { namespace }) fetchOrderModificationRequestById!: (modificationRequestId: number) => Promise<OrderModificationRequest>;
	@Action('setActiveModificationRequest', { namespace }) private setActiveModificationRequest!: (modificationRequest: OrderModificationRequest) => void;
	@Action('addReorderItemsToCart', { namespace }) addReorderItemsToCart!: ({ reorderItems, publishedMenus } : { reorderItems: ReorderItem[]; publishedMenus: Menu[] }) => Promise<boolean>;
	@Action('fetchInquiryFormRequestById', { namespace: 'inquiryForm' }) private fetchInquiryFormRequest!: (id: number) => Promise<void>;
	@Action('setSelectedInquiryFormRequest', { namespace: 'inquiryForm' }) private setSelectedInquiryFormRequest!: (request: InquiryFormRequest | null) => void;
	@Action('repeatOrder', { namespace }) repeatOrder!: (orderId: string) => Promise<void>;
	@Getter('getOrderType', { namespace }) orderType!: string;
	@Getter('getOrderDetails', { namespace }) orderDetails!: OrderDetails;
	@Getter('getSelectedItem', { namespace }) selectedItem!: MenuItem;
	@Getter('getOrderValidationErrors', { namespace }) orderValidationErrors!: string[];
	@Getter('getModifyingOrder', { namespace }) isModifyingOrder!: boolean;
	@Getter('getActiveModificationRequest', { namespace }) activeModificationRequest!: OrderModificationRequest | null;
	@Getter('getRestaurant', { namespace: 'auth' }) restaurant!: Restaurant;
	@Getter('hasPermission', { namespace: 'auth' }) hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getPinRole', { namespace: 'auth' }) pinRole!: number;
	@Getter('getCheckinQuestion', { namespace: 'auth' }) checkinQuestion!: CheckinQuestion;
	@Getter('getPublishedMenus', { namespace: 'menus' }) publishedMenus!: Menu[];
	@Getter('getSelectedInquiryFormRequest', { namespace: 'inquiryForm' }) private inquiryFormRequest!: InquiryFormRequest;

	private storage: Storage = new Storage();
	private orderDetailsModalType: string = '';
	private returnPath: string = '';
	private showGuestDetailsModal: boolean = false;
	private showWarningModal: boolean = false;
	private showSubmittedModal: boolean = false;
	private showCheckinQuestionModal: boolean = false;
	private cartOpen: boolean = false;
	private showPinScreen: boolean = false;
	private isDataFetching: boolean = false;
	private isReorderLoading: boolean = false;
	private isTableInfoFetchedByApp: boolean = false;
	private pinTimer: number | null = null;
	private pinRoles: PinRoles = PINROLES;
	private kioskWelcomeDismissed = false;

	private get isUserFromAPP(): boolean {
		return !!window.B2BApp;
	}

	/**
	 * Get the options for the order type select in the order details modal
	 *
	 * @return { { text: string, value: string }[] }
	 */
	private get orderTypeOptions(): { text: string, value: string }[] {
		const hasScheduledMenus = this.publishedMenus.some((menu: Menu) => menu.on_demand || menu.scheduled);
		const hasDineIn = this.publishedMenus.some((menu: Menu) => menu.dine_in);
		const hasCatering = this.restaurant.catering && hasScheduledMenus;
		const hasTakeOut = !hasCatering && hasScheduledMenus;
		const hasEvents = (this.pinRole <= this.pinRoles.events || !!this.orderDetails.checkNum) && this.publishedMenus.some((menu: Menu) => menu.event_package);

		// BOWLERO ONLY - Bowlero have lane reservation which is essentially the same as dine-in but with a different name and sales type
		// TODO: Remove the restaurant.group check when all Bowlero locations are ready to use restaurant.groups
		const isBowleroGroup = this.restaurant.group === 'bowlero' || !!this.restaurant.groups?.includes('bowlero');
		const hasLaneRes = isBowleroGroup && hasDineIn;

		return [
			hasDineIn && { text: isBowleroGroup ? this.$t('orders.management.parent_card.header.dine_in_retail_label') : this.$t('orders.management.parent_card.header.dine_in_label'), value: 'dine-in' },
			hasLaneRes && { text: this.$t('orders.management.parent_card.header.lane_res_label') as string, value: 'lane-res' },
			hasTakeOut && { text: this.$t('orders.management.parent_card.header.takeout_label') as string, value: 'take-out' },
			hasEvents && { text: this.$t('orders.management.parent_card.header.event_label') as string, value: 'events' },
			hasCatering && { text: this.$t('orders.management.parent_card.header.generic_catering_label') as string, value: 'generic-catering' }
		].filter((option: false | { text: string, value: string }) => option) as { text: string, value: string }[];
	}

	/**
	 * Filter the menus based on the order type and if they have items or sections
	 *
	 * @return {Menu[]}
	 */
	private get filteredMenus(): Menu[] {
		return this.publishedMenus.filter((menu: Menu) => {
			// Check if the menu has items or sections
			if ((menu.items?.length || menu.sections?.length) && (menu.access_level === 'unrestricted' || menu.access_level === 'staff' || (menu.access_level === 'operator' && this.restaurant.catering))) {
				// Check if the menu is available for the selected order type
				if (this.orderType === 'dine-in') {
					// If this is a menu manager event, show all event menus
					if(this.orderDetails.menuManagerEvent) {
						return menu.dine_in && menu.event_package;
					}
					return menu.dine_in && !menu.event_package;
				}
				else if (this.orderType === 'take-out' || this.orderType === 'generic-catering') {
					return menu.on_demand || menu.scheduled;
				}
				else {
					return true;
				}
			}
			return false;
		});
	}

	private get kioskMode(): { isActive: boolean; isEdit: boolean } {
		const isKiosk = this.hasPermission(PERMISSION.SESSION_KIOSK);
		const isEdit = isKiosk && !!this.$route.params.order_id;
		return {
			isActive: isKiosk,
			isEdit
		};
	}

	private get showKioskWelcome(): boolean {
		return (
			this.kioskMode.isActive && !this.kioskMode.isEdit && !this.showPinScreen && !this.kioskWelcomeDismissed
		);
	}

	private beforeRouteEnter(_to: Route, from: Route, next: NavigationGuardNext<Orders>) {
		next(vm => vm.returnPath = from.path);
	}

	/**
	 * Fetch the published menus, set the restaurant charges and set the mode
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		try {
			this.isDataFetching = true;

			this.setModifyingOrder(!this.$route.path.includes('create'));

			await this.fetchPublishedMenus();
			this.setRestaurantCharges(this.restaurant);

			// Clean up abandoned open items when entering the ordering flow
			if (this.hasPermission(PERMISSION.ORDER_ADD_OPEN_ITEM)) {
				this.cleanupOpenItems();
			}

			if (this.isModifyingOrder || this.kioskMode.isEdit) {
				await this.fetchAndSetOrderInfo(this.$route.params.order_id);
				window.addEventListener('beforeunload', this.handleBeforeUnload);

				if (this.orderValidationErrors && Object.keys(this.orderValidationErrors).length > 0) {
					this.showWarningModal = true;
				}

				if (!this.activeModificationRequest && this.$route.query.modificationRequest) {
					this.fetchAndSetModificationRequest();
				}
			}
			else {
				this.orderDetailsModalType = 'create';
				if(window.B2BApp?.getTableNum()) {
					this.ingestAppTableData();
				}

				// Clean up abandoned open items when entering the ordering flow
				if (this.hasPermission(PERMISSION.ORDER_ADD_OPEN_ITEM)) {
					await this.cleanupOpenItems();
				}
			}

			if(window.B2BApp?.getMemberInfo()) {
				const memberInfo: CheckoutMemberInfo = JSON.parse(window.B2BApp.getMemberInfo());
				// The app serializes promosApplied as an array; the store keeps it as a Set
				const appliedPromos: string[] = (memberInfo.promosApplied as unknown as string[]) || [];
				const hasAppliedDiscount: boolean = !!appliedPromos.length;

				this.lookupMembership({ identifier: memberInfo.identifier }).then(result => {
					if (!result?.member?.id) {
						return;
					}
					// Restore a discount already applied to the check so its provider stays
					// locked (and pre-selected) when adding more items
					if (hasAppliedDiscount) {
						const group = result.providers.find(providerGroup =>
							(providerGroup.promos as (MemberPromo | PunchhPromo)[]).some(promo => appliedPromos.includes(String(promo.id)))
						);
						if (group) {
							const selectedPromos = (group.promos as (MemberPromo | PunchhPromo)[]).filter(promo => appliedPromos.includes(String(promo.id)));
							this.applyPromoCode({ memberInfo: buildMemberInfoFromProvider(result, group, selectedPromos), menus: this.publishedMenus });
						}
					}
				});
			}
			const inquiryRequestId = Number(this.$route.query?.inquiryRequestId);
			if (inquiryRequestId && this.inquiryFormRequest?.id !== inquiryRequestId) {
				await this.fetchInquiryFormRequest(inquiryRequestId);
			}
			else if (!inquiryRequestId) {
				this.setSelectedInquiryFormRequest(null);
			}
			this.setIsQuoting(!!inquiryRequestId);
			this.handlePinScreen();
		}
		catch (errorMessage) {
			Toast.error(this.$t('shared.error_fetching_menus', { errorMessage }));
		}
		finally {
			this.isDataFetching = false;
		}

		// Apply theme brand specifically for Orders component
		this.applyThemeBrand();
	}

	private beforeRouteLeave(to: any, from: any, next: any): void {
		this.resetOrderState();
		next();
	}

	private beforeDestroy(): void {
		window.removeEventListener('beforeunload', this.handleBeforeUnload);
		window.removeEventListener('click', () => this.pinTimer = null);
		window.removeEventListener('touchend', () => this.pinTimer = null);
		window.removeEventListener('scroll', () => this.pinTimer = null);

		// Reset theme brand when leaving Orders component
		document.documentElement.setAttribute('data-brand', 'default');
	}

	private applyThemeBrand(): void {
		const theme = this.restaurant?.menu_configuration?.theme?.trim().toLowerCase();
		document.documentElement.setAttribute('data-brand', theme || 'default');
	}

	/**
	 * If the user is coming from the app and we are getting table info, we need to fill out what we have
	 * so the user can skip the order details modal and go straight to the ordering.
	 *
	 * @return {void}
	 */
	private ingestAppTableData(): void {
		const orderType: string | undefined = window.B2BApp.getOrderType?.();
		const tableNum: string | undefined = window.B2BApp.getTableNum();
		let tableLocation: string | undefined = window.B2BApp.getTableLocation();
		let tableArea: string | undefined = window.B2BApp.getTableArea();
		const checkNum: string | undefined = window.B2BApp.getCheckNum();
		const menuManagerEvent: boolean | undefined = orderType === 'events' || undefined;
		const menuManagerEventPackage : string | undefined = window.B2BApp.getEventPackage?.() || undefined;

		if (!!tableNum && (!tableLocation || !tableArea)) {
			// this table came from the PoS, do a reverse table location and area lookup
			this.restaurant.generic_table_locations?.custom!.some((table: CustomGenericTableLocations) => {
				return table.areas.split(',').some((areaStr: string) => {
					const [area, areaTableNum] = areaStr.split(':').map(s => s.trim());
					if (areaTableNum === tableNum) {
						tableArea = area;
						tableLocation = table.location;
						return true;
					}
				});
			});
		}

		this.setOrderDetails({
			takeoutType: '',
			type: 'dine-in',
			tableNum,
			tableLocation,
			tableArea,
			checkNum,
			menuManagerEvent,
			menuManagerEventBEO: window.B2BApp.getEventBEO?.() || undefined,
			menuManagerEventPackage,
			menuManagerLaneReservation: orderType === 'lane_reservation' || undefined
		});
		this.isTableInfoFetchedByApp = true;

		this.ingestAppReorderItems();

		// Show order details modal for event orders (new check) unless adding to an
		// existing check (checkNum exists) and a package has already been selected
		if(!menuManagerEvent || (menuManagerEvent && checkNum && menuManagerEventPackage)) {
			this.handleDetailsModalConfirm();
		}
	}

	/**
	 * Read the reorder payload from the app bridge and add those items to the cart.
	 * Shows an error toast if the payload is missing items or any item is unavailable.
	 *
	 * @return {Promise<void>}
	 */
	private async ingestAppReorderItems(): Promise<void> {
		const reorderRaw: string | null | undefined = window.B2BApp.getReorderItems?.();
		if (!reorderRaw) {
			return;
		}

		try {
			this.isReorderLoading = true;
			const reorderItems: ReorderItem[] = JSON.parse(reorderRaw);
			const added: boolean = Array.isArray(reorderItems)
				&& await this.addReorderItemsToCart({ reorderItems, publishedMenus: this.publishedMenus });
			if (!added) {
				Toast.error(this.$t('orders.reorder.unavailable_error'));
			}
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.reorder.add_error', { errorMessage: toErrorMessage(errorMessage) }));
		}
		finally {
			this.isReorderLoading = false;
		}
	}

	/**
	 * Load the order being repeated into the create order state. On failure the operator is left on
	 * a usable, empty create order screen rather than being bounced back.
	 *
	 * @param {string} orderId - the source order's id
	 * @return {Promise<void>}
	 */
	private async repeatSourceOrder(orderId: string): Promise<void> {
		try {
			this.isReorderLoading = true;
			await this.repeatOrder(orderId);
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.repeat_order.load_error', { errorMessage: toErrorMessage(errorMessage) }));
			this.resetOrderState();
			this.orderDetailsModalType = 'create';
		}
		finally {
			this.isReorderLoading = false;
		}
	}

	/**
	 * Delete the current order by resetting the order state and setting the modal type to create,
	 * so that the order details modal button text is correct
	 * We don't actually delete anything because the order is not saved until the user submits it
	 *
	 * @return {void}
	 */
	private handleDeleteOrder(): void {
		this.resetOrderState();

		if (this.inquiryFormRequest) {
			this.setSelectedInquiryFormRequest(null);
		}

		this.orderDetailsModalType = 'create';
	}

	/**
	 * Hide guest details modal and show submitted modal after order is submitted
	 *
	 * @return {void}
	 */
	private handleOrderSubmitted(): void {
		this.showGuestDetailsModal = false;
		this.showSubmittedModal = true;
		const inquiryRequestId = Number(this.$route.query?.inquiryRequestId);
		if (inquiryRequestId) {
			this.fetchInquiryFormRequest(inquiryRequestId);
		}
	}

	/**
	 * Handle the beforeunload event to show a warning if the user tries to leave the page
	 * This is to prevent the user from losing their order if they accidentally navigate away or refresh the page
	 * This only triggers if the user has filled out the order details (i.e. the order has been updated)
	 *
	 * @param {BeforeUnloadEvent} e
	 * @return {void}
	 */
	private handleBeforeUnload(e: BeforeUnloadEvent): void {
		// Do not show this prompt if on the app (for now)
		if(window.B2BApp) {
			return;
		}
		if (process.env.NODE_ENV !== 'development') {
			e.preventDefault();
			e.returnValue = '';
		}
	}

	/**
	 * Hide submitted modal and show order details modal with create type
	 *
	 * @return {void}
	 */
	private handleNewOrder(): void {
		window.removeEventListener('beforeunload', this.handleBeforeUnload);
		this.showSubmittedModal = false;
		this.orderDetailsModalType = 'create';
	}

	/**
	 * Handle the confirm event from the order details modal
	 * Adds an event listener to show a warning if the user tries to leave the page
	 * Shows checkin question modal for Bowlero locations if configured
	 * Loads the order being repeated, now that there is an order type to price it against, once only
	 *
	 * @return {Promise<void>}
	 */
	private async handleDetailsModalConfirm(): Promise<void> {
		this.orderDetailsModalType = '';
		window.addEventListener('beforeunload', this.handleBeforeUnload);

		const { repeatFrom, ...query } = this.$route.query;
		if (repeatFrom) {
			this.$router.replace({ query }).catch(() => {});
			await this.repeatSourceOrder(String(repeatFrom));
		}

		// Show checkin question modal for Bowlero locations, only on new retail checks
		if (this.checkinQuestion.mapped_items.length && !this.isModifyingOrder && !this.orderDetails.checkNum && !this.orderDetails.menuManagerEvent) {
			this.showCheckinQuestionModal = true;
		}
	}

	/**
	 * If the user is a staff and has pins in Auth0, add a timer to show the pin screen after 60 seconds of inactivity
	 * Also add event listeners to reset the timer when the user interacts with the page
	 *
	 * @return {void}
	 */
	private handlePinScreen(): void {
		const hasPins = localStorage.getItem('hasPins') === 'true';
		if (hasPins && this.hasPermission(PERMISSION.SESSION_STAFF)) {
			// If the pin screen was open when the user left the page, show it again
			// This prevents the user from refreshing the page to get around the pin screen
			if (localStorage.getItem('pinEntered') === 'false') {
				this.displayPinScreen();
			}
			else {
				this.resetPinTimer();
			}
			window.addEventListener('click', this.resetPinTimer);
			window.addEventListener('touchend',this.resetPinTimer);
			window.addEventListener('scroll', this.resetPinTimer);
		}
	}

	/**
	 * Display the pin screen, set pinEntered to false in localStorage and clear the pin timer
	 *
	 * @return {void}
	 */
	private displayPinScreen(): void {
		if(!window.B2BApp) {
			this.showPinScreen = true;
		}
		window.removeEventListener('beforeunload', this.handleBeforeUnload);
		this.storage.setUniversal('pinEntered', 'false');
		if (this.pinTimer) {
			clearTimeout(this.pinTimer);
		}
	}

	/**
	 * Reset the pin timer to show the pin screen after 60 seconds of inactivity
	 *
	 * @return {void}
	 */
	private resetPinTimer(): void {
		if (this.pinTimer) {
			clearTimeout(this.pinTimer);
		}
		this.pinTimer = window.setTimeout(this.displayPinScreen, 60000);
	}

	/**
	 * Handle the pin-validated event from the pin screen
	 *
	 * @return {void}
	 */
	private handlePinValidated(): void {
		this.showPinScreen = false;
		this.storage.setUniversal('pinEntered', 'true');
		window.addEventListener('beforeunload', this.handleBeforeUnload);
		this.resetPinTimer();
	}

	/**
	 * Fetch and set the active modification request if it belongs to the current order
	 *
	 * @return {Promise<void>}
	 */
	private async fetchAndSetModificationRequest(): Promise<void> {
		try {
			const modificationRequest = await this.fetchOrderModificationRequestById(+this.$route.query.modificationRequest);

			// Only set active modification request if it belongs to the current order
			if (modificationRequest.order_id !== this.$route.params.order_id) {
				Toast.error(this.$t('orders.management.modification_request_modal.error_request_does_not_belong_to_order'));
				return;
			}
			if (modificationRequest.status !== 'pending') {
				Toast.error(this.$t('orders.management.modification_request_modal.error_request_is_not_pending'));
			}
			this.setActiveModificationRequest(modificationRequest);
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.modification_request_modal.error_fetching_requests', { errorMessage }));
		}
	}

	private decode(str: string): string {
		return decodeString(str);
	}
}
